import api from '.';
import {
  ActivityLogItem,
  CareCircle,
  CareCircleMember,
  CareCircleMemberInvite,
  CareRecipientSummary,
  CreateCareCircleSpec,
  InvitedMember,
  Message,
  PaginatedCareCircles,
  paginationQueryParams,
  PaginationParams,
} from '@karehero/models';

export const careCircleApi = api.injectEndpoints({
  endpoints: (builder) => ({
    createCareCircle: builder.mutation<CareCircle, CreateCareCircleSpec>({
      query: (payload) => ({
        url: '/care-circle',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['CareCircles'],
    }),
    getAll: builder.query<PaginatedCareCircles, PaginationParams | void>({
      query: (params) => ({
        url: '/care-circle',
        params: paginationQueryParams(params),
      }),
      providesTags: ['CareCircles'],
    }),
    getCareCircle: builder.query<CareCircle, string>({
      query: (id) => ({ url: `care-circle/${id}` }),
      providesTags: ['CareCircles'],
    }),
    getSessionCareCircle: builder.query<CareCircle, void>({
      query: () => '/care-circle/session',
      providesTags: ['CareCircles'],
    }),
    getSessionCareCircleMember: builder.query<CareCircleMember, void>({
      query: () => '/care-circle/member',
      providesTags: ['CareCircles'],
    }),
    getByAccount: builder.query<CareCircle[], void>({
      query: () => '/care-circle/account',
      providesTags: ['CareCircles'],
    }),
    getAllCareRecipientSummaryByAccount: builder.query<
      CareRecipientSummary[],
      string
    >({
      query: (accountId: string) =>
        `/care-circle/account/${accountId}/recipient-summaries`,
      keepUnusedDataFor: 0,
    }),
    getCareCircleInvites: builder.query<InvitedMember[], string>({
      query: (careCircleId) => `/care-circle/${careCircleId}/invites`,
      providesTags: ['InvitedMembers'],
    }),
    inviteCareCircle: builder.mutation<Message, CareCircleMemberInvite>({
      query: (payload) => ({
        url: `/care-circle/${payload.careCircleId}/invite`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['CareCircles', 'InvitedMembers'],
    }),
    updateMember: builder.mutation<CareCircleMember, CareCircleMember>({
      query: (careCircleMember) => ({
        url: `/care-circle/member`,
        method: 'PUT',
        body: careCircleMember,
      }),
      invalidatesTags: ['CareCircles'],
    }),
    getCareCircleActivity: builder.query<
      ActivityLogItem[],
      { careCircleId: string; limit?: number; offset?: number }
    >({
      query: ({ careCircleId, limit = 20, offset = 0 }) => ({
        url: `/care-circle/${careCircleId}/activity`,
        params: { limit, offset },
      }),
    }),
  }),
});

export const {
  useGetAllQuery,
  useGetCareCircleQuery,
  useGetSessionCareCircleQuery,
  useGetSessionCareCircleMemberQuery,
  useLazyGetAllCareRecipientSummaryByAccountQuery,
  useGetCareCircleInvitesQuery,
  useInviteCareCircleMutation,
  useUpdateMemberMutation,
  useGetByAccountQuery,
  useLazyGetByAccountQuery,
  useCreateCareCircleMutation,
  useGetCareCircleActivityQuery,
} = careCircleApi;

export const invalidateCareCircleTags = () => {
  return careCircleApi.util.invalidateTags(['CareCircles']);
};

export default careCircleApi;
